import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Activity, TrendingUp, Dumbbell, Trash2, Calendar, Target, Zap } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const FitnessTracker = () => {
  const navigate = useNavigate();
  const [exercises, setExercises] = useState([]);
  const [workoutLogs, setWorkoutLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    exercise_id: '',
    sets: 3,
    reps: 10,
    weight: 0,
    duration_minutes: null,
    notes: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to access fitness tracker');
      navigate('/login');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [exercisesRes, logsRes, statsRes] = await Promise.all([
        api.get('/fitness/exercises'),
        api.get('/fitness/workouts'),
        api.get('/fitness/stats')
      ]);
      setExercises(exercisesRes.data);
      setWorkoutLogs(logsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load fitness data');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const submitData = {
        ...formData,
        sets: parseInt(formData.sets),
        reps: parseInt(formData.reps),
        weight: parseFloat(formData.weight),
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null
      };
      
      await api.post('/fitness/workouts', submitData);
      toast.success('Workout logged successfully!');
      setFormData({
        exercise_id: '',
        sets: 3,
        reps: 10,
        weight: 0,
        duration_minutes: null,
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log workout');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (workoutId) => {
    try {
      await api.delete(`/fitness/workouts/${workoutId}`);
      toast.success('Workout deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete workout');
    }
  };

  const categories = ['all', ...new Set(exercises.map(e => e.category))];
  const filteredExercises = selectedCategory === 'all'
    ? exercises
    : exercises.filter(e => e.category === selectedCategory);

  return (
    <div className="min-h-screen bg-background p-6 md:p-12">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-heading font-black uppercase tracking-tight mb-2" data-testid="fitness-tracker-title">
              Fitness Tracker
            </h1>
            <p className="text-muted-foreground">Track your workouts and monitor your progress</p>
          </div>
          <Link to="/dashboard">
            <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
              Back to Dashboard
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-card/50 border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center">
                  <Activity className="w-4 h-4 mr-2 text-primary" />
                  Total Workouts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold" data-testid="total-workouts">{stats.total_workouts}</p>
              </CardContent>
            </Card>

            <Card className="bg-card/50 border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-secondary" />
                  Last 7 Days
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold" data-testid="recent-workouts">{stats.recent_workouts}</p>
              </CardContent>
            </Card>

            <Card className="bg-card/50 border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center">
                  <Dumbbell className="w-4 h-4 mr-2 text-primary" />
                  Total Volume (kg)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold" data-testid="total-volume">{stats.total_volume.toLocaleString()}</p>
              </CardContent>
            </Card>

            <Card className="bg-card/50 border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center">
                  <Zap className="w-4 h-4 mr-2 text-secondary" />
                  Avg Per Week
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{Math.round((stats.recent_workouts / 7) * 7)}</p>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Log Workout Form */}
          <Card className="bg-card/50 border-white/10">
            <CardHeader>
              <CardTitle className="font-heading uppercase flex items-center">
                <Target className="w-5 h-5 mr-2" />
                Log Workout
              </CardTitle>
              <CardDescription>Record your exercise session</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="exercise_id">Exercise</Label>
                  <Select
                    name="exercise_id"
                    value={formData.exercise_id}
                    onValueChange={(value) => setFormData({ ...formData, exercise_id: value })}
                    required
                  >
                    <SelectTrigger className="bg-background/50 border-white/10" data-testid="exercise-select">
                      <SelectValue placeholder="Select exercise" />
                    </SelectTrigger>
                    <SelectContent>
                      {exercises.map(exercise => (
                        <SelectItem key={exercise.id} value={exercise.id}>
                          {exercise.name} ({exercise.muscle_group})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="sets">Sets</Label>
                    <Input
                      id="sets"
                      name="sets"
                      type="number"
                      value={formData.sets}
                      onChange={handleInputChange}
                      min="1"
                      required
                      className="bg-background/50 border-white/10"
                      data-testid="sets-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="reps">Reps</Label>
                    <Input
                      id="reps"
                      name="reps"
                      type="number"
                      value={formData.reps}
                      onChange={handleInputChange}
                      min="1"
                      required
                      className="bg-background/50 border-white/10"
                      data-testid="reps-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="weight">Weight (kg)</Label>
                    <Input
                      id="weight"
                      name="weight"
                      type="number"
                      step="0.5"
                      value={formData.weight}
                      onChange={handleInputChange}
                      min="0"
                      required
                      className="bg-background/50 border-white/10"
                      data-testid="weight-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="duration_minutes">Duration (minutes) - Optional</Label>
                  <Input
                    id="duration_minutes"
                    name="duration_minutes"
                    type="number"
                    value={formData.duration_minutes || ''}
                    onChange={handleInputChange}
                    min="1"
                    placeholder="e.g., 30"
                    className="bg-background/50 border-white/10"
                  />
                </div>

                <div>
                  <Label htmlFor="notes">Notes - Optional</Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="How did it feel? Any observations?"
                    className="bg-background/50 border-white/10"
                    rows={3}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary/90"
                  data-testid="log-workout-btn"
                >
                  {loading ? 'Logging...' : 'Log Workout'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Top Exercises */}
          {stats && stats.top_exercises.length > 0 && (
            <Card className="bg-card/50 border-white/10">
              <CardHeader>
                <CardTitle className="font-heading uppercase flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Top Exercises
                </CardTitle>
                <CardDescription>Your most performed exercises</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats.top_exercises.map((exercise, idx) => (
                    <div key={idx} className="flex justify-between items-center pb-3 border-b border-white/10">
                      <div>
                        <p className="font-semibold">{exercise.name}</p>
                        <p className="text-sm text-muted-foreground">{exercise.count} sessions</p>
                      </div>
                      <div className="text-right">
                        <div className="w-32 bg-muted rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full"
                            style={{ width: `${(exercise.count / stats.top_exercises[0].count) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Workout History */}
        <Card className="bg-card/50 border-white/10 mt-8">
          <CardHeader>
            <CardTitle className="font-heading uppercase">Workout History</CardTitle>
            <CardDescription>Your recent exercise sessions</CardDescription>
          </CardHeader>
          <CardContent>
            {workoutLogs.length === 0 ? (
              <div className="text-center py-12">
                <Activity className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No workouts logged yet. Start tracking!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {workoutLogs.map(log => (
                  <div
                    key={log.id}
                    className="flex justify-between items-start border-b border-white/10 pb-3"
                    data-testid="workout-log-item"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{log.exercise_name}</p>
                        <span className="text-xs px-2 py-1 bg-primary/20 text-primary rounded">
                          {log.sets} × {log.reps} @ {log.weight}kg
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {new Date(log.date).toLocaleDateString()} at {new Date(log.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      {log.notes && (
                        <p className="text-sm text-muted-foreground italic mt-1">{log.notes}</p>
                      )}
                      {log.duration_minutes && (
                        <p className="text-sm text-muted-foreground mt-1">Duration: {log.duration_minutes} min</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(log.id)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      data-testid="delete-workout-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FitnessTracker;
